import { useState, useEffect } from "react";
import type { ParsedCountry, CountryIso2 } from "../types/PhoneInput";
import { getCountryByIso2, getAllCountries } from "../types/PhoneInput";
import {
  formatPhoneNumber,
  removePhoneFormatting,
  getPhoneDisplayText,
  getValidPhoneLengths,
} from "../utils/validation";

interface UsePhoneInputOptions {
  defaultCountry?: CountryIso2;
  onCountryChange?: (country: ParsedCountry) => void;
  onPhoneChange?: (
    phone: string,
    formatted: string,
    country: ParsedCountry
  ) => void;
}

interface UsePhoneInputResult {
  // State
  phone: string;
  setPhone: (phone: string) => void;
  country: ParsedCountry | null;
  setCountry: (country: ParsedCountry) => void;

  // Formatting
  formatted: string | null;
  international: string | null;

  // Validation
  isValid: boolean;
  error: string | null;
  expectedLengths: number[];

  // Display
  rawDigits: string;
  displayText: string;

  // Utilities
  guessCountryFromPhone: (phone: string) => ParsedCountry | undefined;
  allCountries: ParsedCountry[];
  reset: () => void;

  // For form submission
  getSubmitValue: () => string | null; // Returns international format
}

// ✅ Helper to detect if a country has a format/pattern
const hasPattern = (c: ParsedCountry | null): boolean => {
  if (!c) return false;
  return c.format !== undefined && c.format !== null && c.format !== "";
};

export const usePhoneInput = (
  options: UsePhoneInputOptions = {}
): UsePhoneInputResult => {
  const { defaultCountry = "ae", onCountryChange, onPhoneChange } = options;

  const [phone, setPhoneState] = useState<string>("");
  const [country, setCountryState] = useState<ParsedCountry | null>(
    getCountryByIso2(defaultCountry) || null
  );

  // ✅ Update country when defaultCountry prop changes
  useEffect(() => {
    const newCountry = getCountryByIso2(defaultCountry);
    if (newCountry && country?.iso2 !== newCountry.iso2) {
      setCountryState(newCountry);
      setPhoneState(""); // reset when default country changes
    }
  }, [defaultCountry]);

  // Get display text (formatting + validation info from utils)
  const display = getPhoneDisplayText(phone, country);

  // ✅ Only compute expected lengths & validation when a pattern exists
  const shouldValidate = hasPattern(country);
  const expectedLengths = shouldValidate ? getValidPhoneLengths(country) : [];

  // ✅ Generate error message ONLY for pattern countries
  let error: string | null = null;

  // Handle phone change
  const setPhone = (newPhone: string) => {
    setPhoneState(newPhone);

    if (country) {
      const formatted = formatPhoneNumber(newPhone, country);
      onPhoneChange?.(newPhone, formatted || "", country);
    }
  };

  // Handle country change
  const setCountry = (newCountry: ParsedCountry) => {
    setCountryState(newCountry);
    onCountryChange?.(newCountry);

    // Reset phone when country changes internally
    setPhoneState("");

    // Re-validate / re-format if phone exists (optional)
    if (phone && country !== newCountry) {
      const formatted = formatPhoneNumber(phone, newCountry);
      if (formatted) {
        onPhoneChange?.(phone, formatted, newCountry);
      }
    }
  };

  // Guess country from dial code
  const guessCountryFromPhone = (
    phoneNumber: string
  ): ParsedCountry | undefined => {
    const digits = removePhoneFormatting(phoneNumber);
    const allCountriesList = getAllCountries();

    // Try to find by dial code (longest match first)
    const matches = allCountriesList
      .filter((c) => digits.startsWith(c.dialCode))
      .sort((a, b) => b.dialCode.length - a.dialCode.length);

    return matches[0];
  };

  const getSubmitValue = (): string | null => {
    // ✅ For no-pattern countries, accept any digits (up to 15 at input level)
    if (!country) return null;
    if (shouldValidate && !display.isValid) return null;

    const digitsOnly = removePhoneFormatting(phone);
    if (!digitsOnly) return null;

    const international = `+${country.dialCode}${digitsOnly}`;
    return international;
  };

  const reset = () => {
    setPhoneState("");
    setCountryState(getCountryByIso2(defaultCountry) || null);
  };

  return {
    // State
    phone,
    setPhone,
    country,
    setCountry,

    // Formatting
    formatted: display.formatted,
    international: display.international,

    // Validation
    isValid: shouldValidate ? display.isValid : true, // ✅ always valid if no pattern
    error,
    expectedLengths,

    // Display
    rawDigits: display.raw,
    displayText: display.formatted || phone,

    // Utilities
    guessCountryFromPhone,
    allCountries: getAllCountries(),
    reset,

    // For form submission
    getSubmitValue,
  };
};
